import { api } from 'redux-store/api';
import { IGetUnviewedCountsArgs, projectsApi, UNVIEWED_ITEMS_SERIALIZATION_KEY } from '../projects';
import queryString from 'query-string';
import isEqual from 'lodash.isequal';
// types
import {
  IActivityFeedItemsArgs,
  TActivityFeedPosts,
  IActivityFeedPost,
  IGetPostByIdArgs,
  ICreatePostBody,
  IDeletePostsBody,
  IUpdatePostBody,
  TActivityFeedComments,
  IGetCommentsArgs,
  IActivityFeedComment,
  ICreateCommentBody,
  IUpdateCommentBody,
  IDeleteCommentsBody,
  TActivityFeedItemLikes,
  IActivityFeedItemLikesArgs,
  ILikeActivityFeedItemArgs,
  IActivityFeedUnviewedItemsArgs,
  IActivityFeedUnviewedItemsCount,
  ICreateItemsViewsBody,
  ICreateItemsViewsBulkBody,
  ICreateItemsViewsResponse,
  IGetPostAudienceArgs,
  IGetProjectAudienceArgs,
  IActivityFeedAudience,
  TGetRecentActivityResponse,
  IGetRecentActivityArgs,
  ISpecificUsers,
  ISpecificUsersArgs,
  IGetNotificationsArgs,
  TGetNotificationsResponse,
  ICreatePostAudienceBody,
  IGetUnviewedNotificationsArgs,
  IActivityFeedAppliableFilters,
  IGetActivityFeedAppliableFiltersArgs,
  TActivityFeedMediaMapped,
  IGetActivityFeedMediaArgs,
  IDeleteCustomAudienceBody,
  IGetActivityFeedDraftsArgs,
  TGetActivityFeedDraftsResponse,
  ActivityFeedStatusEnum,
  ActivityFeedPostTypeEnum,
} from './';
import {
  addSearchComments,
  deleteAllComments,
  deleteComments,
  editComment,
  likeUnlikeComment,
} from 'redux-store/slices/new-comments-slice';
import { activityFeedMediaMapping } from '../../mappings/activity-feed-media';
import { convertSearchComments, isStatusEqualToDraft, mergeActivityFeedMedia } from './helpers';
import { RootState } from 'redux-store/store';
import { setPrevSearch } from 'redux-store/slices/activity-feed-search-slice';
import { deleteDraftUnreadId } from 'redux-store/slices/draft-slice';
import { PatchCollection } from '@reduxjs/toolkit/dist/query/core/buildThunks';
import { IGetHashtagSuggestions, IGetHashtagSuggestionsArgs } from './hashtags.types';

enum Endpoints {
  ACTIVITY_FEED = 'activity-feed/',
  ACTIVITY_FEED_POSTS = 'activity-feed/post',
  ACTIVITY_FEED_COMMENTS = 'activity-feed-post/comments',
  ACTIVITY_FEED_LIKES = 'activity-feed/like',
  ACTIVITY_FEED_VIEWS = 'activity-feed/items/views',
  ACTIVITY_FEED_MESSAGES = 'activity-feed/items/messages',
  ACTIVITY_FEED_AUDIENCE = '/activity-feed-post/audience',
  ACTIVITY_FEED_NOTIFICATIONS = 'notification/',
  ACTIVITY_FEED_MEDIA = 'activity-feed-media/',
  ACTIVITY_FEED_DRAFTS = 'activity-feed/items/drafts',
}

export const activityFeedApi = api.injectEndpoints({
  endpoints: (build) => ({
    getHashtagSuggestions: build.query<IGetHashtagSuggestions, IGetHashtagSuggestionsArgs>({
      query: ({ projectId, ...queryArgs }) => ({
        url: `${Endpoints.ACTIVITY_FEED}mentions/hashtag-suggestions/${projectId}`,
        params: queryArgs,
      }),
      keepUnusedDataFor: 0,
    }),

    getPosts: build.query<TActivityFeedPosts, IActivityFeedItemsArgs>({
      query: ({ projectId, posterProfileIds, ...queryArgs }) => ({
        url: `${Endpoints.ACTIVITY_FEED}${projectId}/posts${
          posterProfileIds
            ? '?' + queryString.stringify({ posterProfileIds }, { arrayFormat: 'none' })
            : ''
        }`,
        params: queryArgs,
      }),
      onQueryStarted: async ({ search = null }, { queryFulfilled, dispatch, getState }) => {
        try {
          const state = getState() as RootState;
          const { prevSearch } = state.activityFeedSearch;
          const normalizedSearchTerm = search ? search.trim() : search;
          // Check if the search term has changed
          if (normalizedSearchTerm !== prevSearch) {
            dispatch(deleteAllComments()); // Clear existing comments
            dispatch(setPrevSearch(search)); // Update the previous search term
          }

          // Exit early if no search term is provided
          if (!normalizedSearchTerm) return;

          // Process the query results
          const { data } = await queryFulfilled;
          const comments = convertSearchComments(data);
          dispatch(addSearchComments(comments));
        } catch (error) {
          console.error('Error processing query results:', error);
        }
      },
      serializeQueryArgs: ({ queryArgs: { projectId } }) => {
        return { projectId };
      },
      merge: (currentCache, newCache, { arg: { cursor } }) => {
        if (!cursor) {
          return newCache;
        }
        return {
          meta: newCache.meta,
          items: [...currentCache.items, ...newCache.items],
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        return !isEqual(currentArg, previousArg);
      },
      keepUnusedDataFor: 0,
    }),

    getPostById: build.query<IActivityFeedPost, IGetPostByIdArgs>({
      query: ({ postId, projectId }) => ({
        url: `${Endpoints.ACTIVITY_FEED}${projectId}/post/${postId}`,
      }),
      keepUnusedDataFor: 0,
    }),

    getPostByIdForCommentDeletion: build.query<IActivityFeedPost, IGetPostByIdArgs>({
      query: ({ postId, projectId }) => ({
        url: `${Endpoints.ACTIVITY_FEED}${projectId}/post/${postId}`,
      }),
      keepUnusedDataFor: 0,
    }),

    createPost: build.mutation<IActivityFeedPost, ICreatePostBody>({
      query: (data) => ({ url: Endpoints.ACTIVITY_FEED_POSTS, body: data, method: 'POST' }),
    }),

    updatePost: build.mutation<IActivityFeedPost, IUpdatePostBody>({
      query: ({ wasPreviouslyDraft, ...data }) => ({
        url: Endpoints.ACTIVITY_FEED_POSTS,
        body: data,
        method: 'PATCH',
      }),
      invalidatesTags: (res, err, { status, wasPreviouslyDraft }) =>
        status !== ActivityFeedStatusEnum.DRAFT && !wasPreviouslyDraft ? ['Post Audience'] : [],
      onQueryStarted: async (
        { projectId, id, status, wasPreviouslyDraft },
        { dispatch, queryFulfilled }
      ) => {
        if (isStatusEqualToDraft(status)) return;

        try {
          const { data: response } = await queryFulfilled;

          if (wasPreviouslyDraft) {
            dispatch(
              activityFeedApi.util.updateQueryData('getDrafts', undefined, (draft) => {
                if (response.data.activityFeedItemId && draft?.items) {
                  draft.items = draft.items.filter(
                    (postDraft) => postDraft.id !== response.data.activityFeedItemId
                  );
                }
              })
            );

            dispatch(deleteDraftUnreadId(response.data.activityFeedItemId));
            return;
          }

          dispatch(
            activityFeedApi.util.updateQueryData('getPosts', { projectId }, (draft) => {
              const index = draft.items.findIndex(
                ({ id: updatedPostId }) => updatedPostId === response.id
              );
              if (index >= 0) {
                Object.assign(draft.items[index], response);
              }
            })
          );
          dispatch(
            activityFeedApi.util.updateQueryData(
              'getPostById',
              { postId: id, projectId },
              (draft) => {
                if (draft?.id === response.id) {
                  Object.assign(draft, response);
                }
              }
            )
          );
        } catch {}
      },
    }),

    deletePost: build.mutation<null, IDeletePostsBody>({
      query: ({ ids, projectId }) => ({
        url: `${Endpoints.ACTIVITY_FEED_POSTS}?projectId=${projectId}&${queryString.stringify(
          { ids: ids },
          { arrayFormat: 'bracket' }
        )}`,
        method: 'DELETE',
      }),
      onQueryStarted: async ({ ids, projectId, status }, { dispatch, queryFulfilled }) => {
        if (isStatusEqualToDraft(status)) return;

        try {
          await queryFulfilled;
          dispatch(
            activityFeedApi.util.updateQueryData('getPosts', { projectId }, (draft) => {
              if (draft?.items) {
                draft.items = draft.items.filter(({ id }) => id !== ids[0]);
              }
            })
          );
        } catch {}
      },
    }),

    getComments: build.query<TActivityFeedComments, IGetCommentsArgs>({
      query: ({ postId, ...queryArgs }) => ({
        url: `${Endpoints.ACTIVITY_FEED_COMMENTS}/${postId}`,
        params: queryArgs,
      }),
      serializeQueryArgs: ({ queryArgs: { postId } }) => {
        return { postId };
      },
      merge: (currentCache, newCache) => {
        return {
          meta: newCache.meta,
          items: [...currentCache.items, ...newCache.items],
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => !isEqual(currentArg, previousArg),
      keepUnusedDataFor: 0,
    }),

    createComment: build.mutation<IActivityFeedComment, ICreateCommentBody>({
      query: ({ projectId: _, addLocal, ...data }) => ({
        url: Endpoints.ACTIVITY_FEED_COMMENTS,
        body: data,
        method: 'POST',
      }),
    }),

    updateComment: build.mutation<IActivityFeedComment, IUpdateCommentBody>({
      query: ({ postId, wasPreviouslyDraft, ...data }) => ({
        url: Endpoints.ACTIVITY_FEED_COMMENTS,
        body: data,
        method: 'PATCH',
      }),
      onQueryStarted: async (
        { postId, id: commentId, status, wasPreviouslyDraft },
        { dispatch, queryFulfilled }
      ) => {
        if (isStatusEqualToDraft(status)) return;

        try {
          const { data: response } = await queryFulfilled;

          if (wasPreviouslyDraft) {
            dispatch(
              activityFeedApi.util.updateQueryData('getDrafts', undefined, (draft) => {
                if (draft?.items) {
                  draft.items = draft.items.filter(({ data }) => data.id !== response.id);
                }
              })
            );
            dispatch(deleteDraftUnreadId(response.id));
            return;
          }

          dispatch(
            activityFeedApi.util.updateQueryData('getComments', { postId }, (draft) => {
              if (draft?.items) {
                if (response?.rootCommentId) {
                  const rootIndex = draft.items.findIndex(
                    ({ id }) => id === response.rootCommentId
                  );
                  const index = draft.items[rootIndex].replies.findIndex(
                    ({ id }) => id === response.id
                  );
                  draft.items[rootIndex].replies[index] = response;
                } else {
                  const index = draft.items.findIndex(({ id }) => id === commentId);
                  if (index >= 0) {
                    const { replies: _, ...restResponse } = response;
                    Object.assign(draft.items[index], restResponse);
                  }
                }
              }
            })
          );
          dispatch(editComment(response));
        } catch {}
      },
    }),

    deleteComment: build.mutation<undefined, IDeleteCommentsBody>({
      query: (data) => ({
        url: `${Endpoints.ACTIVITY_FEED_COMMENTS}?projectId=${
          data.projectId
        }&${queryString.stringify({ ids: data.ids }, { arrayFormat: 'bracket' })}`,
        method: 'DELETE',
      }),
      onQueryStarted: async (
        { postId, projectId, ids, rootCommentId, status },
        { dispatch, queryFulfilled }
      ) => {
        if (isStatusEqualToDraft(status)) return;

        try {
          await queryFulfilled;
          dispatch(
            activityFeedApi.util.updateQueryData('getComments', { postId }, (draft) => {
              if (draft?.items) {
                if (rootCommentId) {
                  const rootCommentIndex = draft.items.findIndex(({ id }) => id === rootCommentId);
                  draft.items[rootCommentIndex].replies = draft.items[
                    rootCommentIndex
                  ].replies.filter(({ id }) => id !== ids[0]);
                } else {
                  draft.items = draft.items.filter(({ id }) => id !== ids[0]);
                }
              }
            })
          );

          const result = await dispatch(
            activityFeedApi.endpoints.getPostByIdForCommentDeletion.initiate(
              { postId, projectId },
              { forceRefetch: true }
            )
          ).unwrap();

          dispatch(
            activityFeedApi.util.updateQueryData('getPosts', { projectId }, (draft) => {
              if (draft?.items) {
                const index = draft.items.findIndex(({ id }) => id === postId);
                if (index >= 0) {
                  draft.items[index] = result;
                }
              }
            })
          );

          dispatch(
            activityFeedApi.util.updateQueryData('getPostById', { postId, projectId }, (draft) => {
              if (draft) {
                Object.assign(draft, result);
              }
            })
          );

          dispatch(
            deleteComments({
              postId,
              rootCommentId,
              id: ids[0],
            })
          );
        } catch {}
      },
    }),

    getRecentActivity: build.query<TGetRecentActivityResponse, IGetRecentActivityArgs>({
      query: (queryArgs) => ({
        url: Endpoints.ACTIVITY_FEED_MESSAGES,
        params: queryArgs,
      }),
      serializeQueryArgs: ({ queryArgs: { projectId } }) => {
        return { projectId };
      },
      merge: (currentCache, newCache) => {
        return {
          meta: newCache.meta,
          items: [...currentCache.items, ...newCache.items],
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => !isEqual(currentArg, previousArg),
      keepUnusedDataFor: 0,
    }),

    getNotifications: build.query<TGetNotificationsResponse, IGetNotificationsArgs>({
      query: (queryArgs) => ({
        url: `${Endpoints.ACTIVITY_FEED_NOTIFICATIONS}${queryArgs.projectId}`,
        params: queryArgs,
      }),
      serializeQueryArgs: ({ queryArgs: { projectId } }) => {
        return { projectId };
      },
      merge: (currentCache, newCache) => {
        return {
          meta: newCache.meta,
          items: [...currentCache.items, ...newCache.items],
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => !isEqual(currentArg, previousArg),
      keepUnusedDataFor: 0,
    }),

    getUnviewedNotifications: build.query<TGetNotificationsResponse, IGetUnviewedNotificationsArgs>(
      {
        query: (queryArgs) => ({
          url: `${Endpoints.ACTIVITY_FEED_NOTIFICATIONS}${queryArgs.projectId}`,
          params: queryArgs,
        }),
        serializeQueryArgs: ({ queryArgs: { projectId } }) => {
          return { projectId };
        },
        merge: (currentCache, newCache) => {
          return {
            meta: newCache.meta,
            items: [...currentCache.items, ...newCache.items],
          };
        },
        forceRefetch: ({ currentArg, previousArg }) => !isEqual(currentArg, previousArg),
        keepUnusedDataFor: 0,
      }
    ),

    getActivityFeedItemLikes: build.query<TActivityFeedItemLikes, IActivityFeedItemLikesArgs>({
      query: ({ activityFeedId, ...queryArgs }) => ({
        url: `${Endpoints.ACTIVITY_FEED_LIKES}/${activityFeedId}`,
        params: queryArgs,
      }),
      serializeQueryArgs: ({ queryArgs: { activityFeedId } }) => {
        return { activityFeedId };
      },
      merge: (currentCache, newCache, { arg: { cursor } }) => {
        if (!cursor) {
          return newCache;
        }
        return {
          meta: newCache.meta,
          items: [...currentCache.items, ...newCache.items],
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => !isEqual(currentArg, previousArg),
      keepUnusedDataFor: 0,
    }),

    likeActivityFeedItem: build.mutation<undefined, ILikeActivityFeedItemArgs>({
      query: ({ postId, commentId, rootCommentId, ...data }) => ({
        url: `${Endpoints.ACTIVITY_FEED_LIKES}/like`,
        body: {
          ...data,
          postId,
        },
        method: 'POST',
      }),
      onQueryStarted: async (
        { commentId, postId, projectId, rootCommentId, activityFeedItemId },
        { dispatch, queryFulfilled }
      ) => {
        const patchList = dispatch(
          commentId
            ? activityFeedApi.util.updateQueryData('getComments', { postId }, (draft) => {
                if (draft?.items) {
                  if (rootCommentId) {
                    const index = draft.items.findIndex(({ id }) => id === rootCommentId);
                    const replyIndex = draft.items[index]?.replies?.findIndex(
                      ({ id }) => id === commentId
                    );
                    if (draft.items[index]?.replies?.[replyIndex]) {
                      draft.items[index].replies[replyIndex].isLiked = true;
                      draft.items[index].replies[replyIndex].likesCount += 1;
                    }
                  } else {
                    const index = draft.items.findIndex(({ id }) => id === commentId);
                    if (index >= 0) {
                      draft.items[index].isLiked = true;
                      draft.items[index].likesCount += 1;
                    }
                  }
                }
              })
            : activityFeedApi.util.updateQueryData('getPosts', { projectId }, (draft) => {
                if (draft?.items) {
                  const index = draft.items.findIndex(({ id }) => id === postId);
                  if (index >= 0 && draft.items[index].type === ActivityFeedPostTypeEnum.POST) {
                    draft.items[index].isLiked = true;
                    draft.items[index].likesCount += 1;
                  }
                }
              })
        );
        let patchPost: PatchCollection;

        if (!commentId) {
          patchPost = dispatch(
            activityFeedApi.util.updateQueryData('getPostById', { postId, projectId }, (draft) => {
              if (draft?.id === postId) {
                draft.isLiked = true;
                draft.likesCount += 1;
              }
            })
          );
        }

        try {
          await queryFulfilled;
          if (commentId) {
            dispatch(
              likeUnlikeComment({
                activityFeedItemId,
                postId,
                projectId,
                commentId,
                rootCommentId,
                increase: true,
              })
            );
          }
        } catch {
          patchList?.undo();
          patchPost?.undo();
        }
      },
    }),

    unlikeActivityFeedItem: build.mutation<undefined, ILikeActivityFeedItemArgs>({
      query: ({ commentId, postId, rootCommentId, ...data }) => ({
        url: `${Endpoints.ACTIVITY_FEED_LIKES}/unlike`,
        body: {
          ...data,
          postId,
        },
        method: 'POST',
      }),
      onQueryStarted: async (
        { commentId, postId, projectId, rootCommentId, activityFeedItemId },
        { dispatch, queryFulfilled }
      ) => {
        const patchList = dispatch(
          commentId
            ? activityFeedApi.util.updateQueryData('getComments', { postId }, (draft) => {
                if (draft?.items) {
                  if (rootCommentId) {
                    const index = draft.items.findIndex(({ id }) => id === rootCommentId);
                    const replyIndex = draft.items[index]?.replies?.findIndex(
                      ({ id }) => id === commentId
                    );
                    if (draft.items[index]?.replies?.[replyIndex]) {
                      draft.items[index].replies[replyIndex].isLiked = false;
                      draft.items[index].replies[replyIndex].likesCount -= 1;
                    }
                  } else {
                    const index = draft.items.findIndex(({ id }) => id === commentId);
                    if (index >= 0) {
                      draft.items[index].isLiked = false;
                      draft.items[index].likesCount -= 1;
                    }
                  }
                }
              })
            : activityFeedApi.util.updateQueryData('getPosts', { projectId }, (draft) => {
                if (draft?.items) {
                  const index = draft.items.findIndex(({ id }) => id === postId);
                  if (index >= 0 && draft.items[index].type === ActivityFeedPostTypeEnum.POST) {
                    draft.items[index].isLiked = false;
                    draft.items[index].likesCount -= 1;
                  }
                }
              })
        );
        let patchPost: PatchCollection;
        if (!commentId) {
          patchPost = dispatch(
            activityFeedApi.util.updateQueryData('getPostById', { postId, projectId }, (draft) => {
              if (draft?.id === postId) {
                draft.isLiked = false;
                draft.likesCount -= 1;
              }
            })
          );
        }
        try {
          await queryFulfilled;
          if (commentId) {
            dispatch(
              likeUnlikeComment({
                activityFeedItemId,
                postId,
                projectId,
                commentId,
                rootCommentId,
                increase: false,
              })
            );
          }
        } catch {
          patchList?.undo();
          patchPost?.undo();
        }
      },
    }),

    getActivityFeedUnviewedItemsCount: build.query<
      IActivityFeedUnviewedItemsCount,
      IActivityFeedUnviewedItemsArgs
    >({
      query: (queryArg) => ({
        url: `${Endpoints.ACTIVITY_FEED_VIEWS}/unviewed-count`,
        params: queryArg,
      }),
    }),

    createItemsViews: build.mutation<ICreateItemsViewsResponse, ICreateItemsViewsBody>({
      query: (data) => ({
        url: Endpoints.ACTIVITY_FEED_VIEWS,
        body: data,
        method: 'POST',
      }),
    }),

    createOverdueItemsViews: build.mutation<ICreateItemsViewsResponse, ICreateItemsViewsBody>({
      query: (data) => ({
        url: `${Endpoints.ACTIVITY_FEED_VIEWS}/overdue-item`,
        body: data,
        method: 'POST',
      }),
      onQueryStarted: async ({ projectId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            projectsApi.util.updateQueryData(
              'getUnviewedCounts',
              { UNVIEWED_ITEMS_SERIALIZATION_KEY } as unknown as IGetUnviewedCountsArgs,
              (draft) => {
                if (draft?.items && draft?.items?.[projectId]?.unviewedOverdue) {
                  draft.items[projectId].unviewedOverdue = 0;
                }
              }
            )
          );
        } catch {}
      },
      invalidatesTags: (_, err, { projectId }) =>
        !err ? [{ type: 'Projects', id: projectId }] : [],
    }),

    createNotificationViewsBulk: build.mutation<undefined, ICreateItemsViewsBulkBody>({
      query: ({ unviewedCountArgs, projectId }) => ({
        url: `${Endpoints.ACTIVITY_FEED_NOTIFICATIONS}view-bulk/${projectId}`,
        method: 'POST',
      }),
      onQueryStarted: async ({ projectId, unviewedCountArgs }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            projectsApi.util.updateQueryData(
              'getUnviewedCounts',
              { UNVIEWED_ITEMS_SERIALIZATION_KEY } as unknown as IGetUnviewedCountsArgs,
              (draft) => {
                if (draft?.items && draft?.items?.[projectId]?.notifications) {
                  draft.items[projectId].notifications = 0;
                }
              }
            )
          );
        } catch {}
      },
    }),

    createMessagesViewsBulk: build.mutation<undefined, ICreateItemsViewsBulkBody>({
      query: ({ unviewedCountArgs, ...data }) => ({
        url: `${Endpoints.ACTIVITY_FEED_VIEWS}/messages/bulk`,
        body: data,
        method: 'POST',
      }),
      onQueryStarted: async (
        { projectId, withoutUpdateUnviewedCount },
        { dispatch, queryFulfilled }
      ) => {
        try {
          await queryFulfilled;
          if (!withoutUpdateUnviewedCount) {
            dispatch(
              projectsApi.util.updateQueryData(
                'getUnviewedCounts',
                { UNVIEWED_ITEMS_SERIALIZATION_KEY } as unknown as IGetUnviewedCountsArgs,
                (draft) => {
                  if (draft?.items && draft?.items?.[projectId]?.messages) {
                    draft.items[projectId].messages = 0;
                  }
                }
              )
            );
          }
        } catch {}
      },
    }),

    getProjectAudience: build.query<IActivityFeedAudience[], IGetProjectAudienceArgs>({
      query: (queryArgs) => ({
        url: `${Endpoints.ACTIVITY_FEED}audience/${queryArgs.projectId}`,
      }),
    }),

    createCustomAudience: build.mutation<null, ICreatePostAudienceBody>({
      query: (body) => ({
        url: `${Endpoints.ACTIVITY_FEED_AUDIENCE}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, err) => (err ? [] : ['Specific Users']),
    }),

    deleteCustomAudience: build.mutation<null, IDeleteCustomAudienceBody>({
      query: ({ projectId, groupId, groupIdToAssign, roleIdToAssign }) => ({
        url: `${Endpoints.ACTIVITY_FEED_AUDIENCE}/${projectId}/${groupId}`,
        method: 'DELETE',
        body: { groupIdToAssign, roleIdToAssign },
      }),
      invalidatesTags: (_, err) => (err ? [] : ['Specific Users', 'Post Audience']),
    }),
    getPostAudience: build.query<ISpecificUsers, IGetPostAudienceArgs>({
      query: (queryArgs) => ({
        url: `${Endpoints.ACTIVITY_FEED_AUDIENCE}/${queryArgs.projectId}/${queryArgs.postId}`,
      }),
      keepUnusedDataFor: 0,
      providesTags: ['Post Audience'],
    }),

    getAudienceGroupAssignments: build.query<
      { hasAssignments: boolean },
      { projectId: number; groupId: number }
    >({
      query: (queryArgs) => ({
        url: `${Endpoints.ACTIVITY_FEED_AUDIENCE}/assignments/${queryArgs.projectId}/${queryArgs.groupId}`,
      }),
      keepUnusedDataFor: 0,
    }),

    getSpecificAudience: build.query<Omit<ISpecificUsers, 'customUsers'>, ISpecificUsersArgs>({
      query: (queryArgs) => ({
        url: `${Endpoints.ACTIVITY_FEED_AUDIENCE}/${queryArgs.projectId}`,
      }),
      providesTags: ['Specific Users'],
    }),

    getAppliableFilters: build.query<
      IActivityFeedAppliableFilters,
      IGetActivityFeedAppliableFiltersArgs
    >({
      query: ({ projectId }) => ({
        url: `${Endpoints.ACTIVITY_FEED}${projectId}/posts/filter`,
      }),
    }),

    getActivityFeedMedia: build.query<TActivityFeedMediaMapped, IGetActivityFeedMediaArgs>({
      query: ({ projectId, ...params }) => ({
        url: `${Endpoints.ACTIVITY_FEED_MEDIA}${projectId}`,
        params,
      }),
      keepUnusedDataFor: 0,
      transformResponse: activityFeedMediaMapping,
      merge: mergeActivityFeedMedia,
      serializeQueryArgs: ({ queryArgs: { projectId } }) => ({ projectId }),
      forceRefetch: ({ currentArg, previousArg }) => !isEqual(currentArg, previousArg),
    }),
    getDrafts: build.query<TGetActivityFeedDraftsResponse, void | IGetActivityFeedDraftsArgs>({
      query: (args?: IGetActivityFeedDraftsArgs) => {
        const { projectIds, ...queryArgs } = args ?? {};

        return {
          url: `${Endpoints.ACTIVITY_FEED_DRAFTS}${
            projectIds ? '?' + queryString.stringify({ projectIds }, { arrayFormat: 'none' }) : ''
          }`,
          params: queryArgs,
        };
      },
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetHashtagSuggestionsQuery,
  useLazyGetHashtagSuggestionsQuery,
  useGetPostsQuery,
  useLazyGetPostsQuery,
  useGetPostByIdQuery,
  useLazyGetPostByIdQuery,
  useCreatePostMutation,
  useUpdatePostMutation,
  useDeletePostMutation,
  useGetCommentsQuery,
  useGetSpecificAudienceQuery,
  useLazyGetCommentsQuery,
  useCreateCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
  useGetActivityFeedItemLikesQuery,
  useLazyGetActivityFeedItemLikesQuery,
  useLikeActivityFeedItemMutation,
  useUnlikeActivityFeedItemMutation,
  useGetActivityFeedUnviewedItemsCountQuery,
  useLazyGetActivityFeedUnviewedItemsCountQuery,
  useCreateItemsViewsMutation,
  useCreateCustomAudienceMutation,
  useCreateMessagesViewsBulkMutation,
  useCreateNotificationViewsBulkMutation,
  useCreateOverdueItemsViewsMutation,
  useGetPostAudienceQuery,
  useGetAudienceGroupAssignmentsQuery,
  useLazyGetAudienceGroupAssignmentsQuery,
  useGetProjectAudienceQuery,
  useLazyGetPostAudienceQuery,
  useLazyGetProjectAudienceQuery,
  useGetRecentActivityQuery,
  useLazyGetRecentActivityQuery,
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useLazyGetSpecificAudienceQuery,
  useDeleteCustomAudienceMutation,
  useGetUnviewedNotificationsQuery,
  useLazyGetUnviewedNotificationsQuery,
  useGetAppliableFiltersQuery,
  useLazyGetAppliableFiltersQuery,
  useGetActivityFeedMediaQuery,
  useLazyGetActivityFeedMediaQuery,
  useGetDraftsQuery,
} = activityFeedApi;
